import { useState, useRef } from "react";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import PermissionGate from "../PermissionGate";
import MapFilter from "./MapFilter";
import Loader from "../Loader";
import "../style/Map.css";

import useDrivers from "./useDrivers";
import useBranches from "./useBranches";
import { drawBranchCircles, clearBranchCircles } from "./branchCircles";

import DriverMarkers from "./DriverMarkers";
import BranchMarkers from "./BranchMarkers";
import DriverInfoWindow from "./DriverInfoWindow";
import BranchInfoWindow from "./BranchInfoWindow";

const MAP_LIBRARIES = ["marker", "geometry"];

const Map = () => {
  const [center] = useState({ lat: 29.2266, lng: 47.9689 });

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [filter, setFilter] = useState({
    activeBranch: false,
    inactiveBranch: false,
    availableDriver: true,
    inDeliveryDriver: true,
    driver: "",
    branch: "",
    deliveryCompany: "",
  });

  const mapRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const {
    drivers,
    counts: driverCounts,
    updateSelectedDriver,
    followTargetId,
  } = useDrivers(filter, selectedDriver, setSelectedDriver);

  const {
    branches,
    counts: branchCounts,
    updateSelectedBranch,
  } = useBranches(filter, selectedBranch, setSelectedBranch);

  const counts = {
    ...driverCounts,
    ...branchCounts,
  };

  const renderOrderStatus = (status) => {
    switch (status) {
      case "0":
        return <div className="badge bg-warning">Pending</div>;
      case "1":
        return <div className="badge bg-warning">Waiting for Pickup</div>;
      case "2":
        return <div className="badge bg-info">Driver Arrived</div>;
      case "3":
      case "4":
        return <div className="badge bg-secondary">Order Scan</div>;
      case "5":
        return <div className="badge bg-success">Order Enroute</div>;
      default:
        return <div className="badge bg-dark">Unknown</div>;
    }
  };

  if (!isLoaded) {
    return (
      <div className="map-wraper position-relative">
        <Loader />
      </div>
    );
  }

  return (
    <div className="map-wraper position-relative">
      <PermissionGate required={"view_driver_map"}>
        <>
          <PermissionGate required={"filter_driver_map"}>
            <MapFilter filter={filter} setFilter={setFilter} counts={counts} />
          </PermissionGate>

          <GoogleMap
            mapContainerClassName="map-container"
            center={center}
            zoom={12}
            onLoad={(map) => (mapRef.current = map)}
            options={{
              mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
              optimized: true,
              streetViewControl: false,
              disableDefaultUI: true,
              gestureHandling: "greedy",
            }}
          >
            <DriverMarkers
              drivers={drivers}
              onSelect={(driverId) => updateSelectedDriver(driverId, false)}
              followTargetId={followTargetId}
              mapRef={mapRef}
              selectedDriver={selectedDriver}
              updateSelectedDriver={setSelectedDriver}
            />
            <BranchMarkers
              branches={branches}
              onSelect={(branch) => {
                clearBranchCircles();
                updateSelectedBranch(branch._id);
                drawBranchCircles(mapRef.current, branch._position);
              }}
              mapRef={mapRef}
            />

            <DriverInfoWindow
              driver={selectedDriver}
              renderOrderStatus={renderOrderStatus}
              onClose={() => {
                setSelectedDriver(null);
              }}
              updateSelectedDriver={updateSelectedDriver}
            />
            <BranchInfoWindow
              branch={selectedBranch}
              renderOrderStatus={renderOrderStatus}
              onClose={() => {
                clearBranchCircles();
                setSelectedBranch(null);
              }}
              updateSelectedBranch={updateSelectedBranch}
            />
          </GoogleMap>
        </>
      </PermissionGate>
    </div>
  );
};

export default Map;